import useLibraryI18n from '@qmulus/ui/i18n/composable.ts';
import {
  Ref, ShallowRef, onBeforeUnmount, onMounted,
} from 'vue';
import {
  Router,
} from 'vue-router';

import {
  useSemanticConfirm,
} from '../use-semantic-confirm.ts';

/**
 * Guards navigation away from the current page while `dirtyFlag` is `true`, asking the user to
 * confirm via a semantic confirm dialog. Also warns before closing or reloading the browser tab,
 * though that second warning always shows the browser's own fixed, generic text — the dialog's
 * translated message cannot be made to appear there.
 *
 * Call it once per page-level component, in `setup()`, with that page's own dirty-state ref and
 * the app's router instance, for example `useConfirmLeave(hasUnsavedData, router)`.
 *
 * Pitfalls:
 * - Only call this once per mounted page. `router.beforeEach` guards are never removed: this
 *   composable discards the unregister function it gets back, so every call that survives a
 *   remount leaves a stale guard behind, still closing over its component's now-detached
 *   `dirtyFlag`. A page that gets mounted and unmounted repeatedly (e.g. opened as a modal) will
 *   accumulate one guard per mount, each able to pop a confirmation for navigations that have
 *   nothing to do with it.
 * - Calling it from more than one simultaneously mounted component also behaves oddly: each
 *   instance registers its own guard, and `vue-router` runs all registered guards, one at a time,
 *   for every navigation. If more than one of their `dirtyFlag`s is `true`, the user is shown
 *   several confirm dialogs back-to-back for a single navigation. Those dialogs all share the one
 *   underlying PrimeVue `ConfirmDialog` (via `useSemanticConfirm` → `useConfirm()`), so only one
 *   can ever be visible at a time — they queue rather than overlap, but the user still has to
 *   click through each in turn.
 */
export function useConfirmLeave(dirtyFlag: Ref<boolean> | ShallowRef<boolean>, router: Router) {
  const {
    t,
  } = useLibraryI18n('composable/use-confirm-leave/use-confirm-leave');

  let resolveNavigation: ((accept: boolean) => void);

  const {
    doAction: onAccept,
  } = useSemanticConfirm({
    variant: 'accept',
    title: () => t('header'),
    message: () => t('message'),
    action: async () => {
      dirtyFlag.value = false;
      resolveNavigation(true);
    },
    reject: async () => resolveNavigation(false),
  });

  router.beforeEach(() => {
    if (!dirtyFlag.value) {
      return true;
    }

    return new Promise((resolve) => {
      resolveNavigation = resolve;
      onAccept();
    });
  });

  function onBeforeUnload(event: BeforeUnloadEvent) {
    if (!dirtyFlag.value) {
      return;
    }

    event.preventDefault();
    // Chrome requires returnValue to be set to trigger its native leave-site prompt.
    event.returnValue = '';
  }

  onMounted(() => {
    window.addEventListener('beforeunload', onBeforeUnload);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', onBeforeUnload);
  });
}
